import type { LeverOverlapDeclaration, LeverOverlapRelation } from './lever-overlap-registry';

/**
 * #5571 UNIT V1 RULING (P1 remediation, terra xhigh @ 0e4b53f7e9): the 14 new
 * SG&A cost-pool lever families (sga-spend.cost-pool-taxonomy-additions.ts)
 * declare themselves receipt-free in their own config, but
 * lever-overlap-registry.ts's EXECUTABLE authority only ever registered the
 * original 8 SG&A families. Every new-vs-existing and new-vs-new pair was
 * UNKNOWN, with no declared relation, while the configs claimed no
 * de-confliction was required.
 *
 * Coordinator ruling: default every unnamed pair to 'overlapping'
 * (receipt-required). Declare 'disjoint' ONLY where structural disjointness
 * is PROVEN from how the charge is booked. False-disjoint silently
 * double-counts every SG&A total; honest-overlapping only forces a
 * de-confliction receipt before the pools are summed.
 *
 * `build5571SgaOverlapDeclarations` is a PURE function - the family universe
 * it needs is passed in by the caller (`lever-overlap-registry.ts`'s
 * `buildDeclarations()`) rather than imported, so this file carries no
 * runtime dependency on that file - only the type-only import above (erased
 * at compile time) - avoiding a circular require with the module that calls it.
 */

const FUNCTIONAL_PERSONNEL_FAMILIES = [
  'personnel_sales_account_management',
  'personnel_operations_carrier_reps',
  'personnel_executive_corporate',
  'personnel_hr_recruiting_training',
] as const;

const BENEFITS_FAMILY = 'personnel_benefits_payroll_taxes_equity';
const INSURANCE_PREMIUMS_FAMILY = 'insurance_premiums';
const CARGO_CLAIMS_SIR_FAMILY = 'cargo_claims_self_insured_retention';
const BAD_DEBT_FAMILY = 'bad_debt_credit_losses';
const DEPRECIATION_FAMILY = 'depreciation_amortization_non_operating';

// Structurally disjoint from EVERY other lever family, including each other:
// bad debt is a distinct GL treatment (AR write-off), not a purchased or
// compensated cost; non-operating D&A is a non-cash, non-operating charge by
// definition. Neither shares dollars with any other lever family's booked charge.
const STRUCTURALLY_DISJOINT_FROM_EVERYTHING: readonly [string, string] = [
  BAD_DEBT_FAMILY,
  DEPRECIATION_FAMILY,
];

// The remaining 12 new families have no proven structural exclusion from the
// rest of the registry - default posture is 'overlapping' (receipt-required).
const DEFAULT_OVERLAPPING_FAMILIES = [
  ...FUNCTIONAL_PERSONNEL_FAMILIES,
  BENEFITS_FAMILY,
  'tech_telecom_data',
  'tech_cloud_infrastructure',
  'finance_accounting_tax_treasury',
  'legal_regulatory_compliance',
  INSURANCE_PREMIUMS_FAMILY,
  CARGO_CLAIMS_SIR_FAMILY,
  'office_purchased_services',
] as const;

// Dedup-guarded emit: the same pair must never be declared twice with
// conflicting relations. Callers push bespoke/named pairs first so they win
// over the later generic-default pass.
type PushDeclaration = (
  familyA: string,
  familyB: string,
  relation: LeverOverlapRelation,
  basis: string
) => void;

function pairKey(familyA: string, familyB: string): string {
  return [familyA, familyB].sort().join('::');
}

function decl(
  familyA: string,
  familyB: string,
  relation: LeverOverlapRelation,
  basis: string
): LeverOverlapDeclaration {
  return Object.freeze({
    familyA,
    familyB,
    relation,
    evidenceRequirement: relation === 'overlapping' ? 'deconfliction_receipt' : 'structural_policy',
    basis,
  });
}

function pushNamedPairs(push: PushDeclaration): void {
  // An SG&A employee sits in exactly one function - the four functional
  // personnel pools are mutually exclusive by construction.
  for (let i = 0; i < FUNCTIONAL_PERSONNEL_FAMILIES.length; i += 1) {
    for (let j = i + 1; j < FUNCTIONAL_PERSONNEL_FAMILIES.length; j += 1) {
      push(
        FUNCTIONAL_PERSONNEL_FAMILIES[i],
        FUNCTIONAL_PERSONNEL_FAMILIES[j],
        'disjoint',
        'An SG&A employee sits in exactly one function; the functional personnel pools are mutually exclusive by construction.'
      );
    }
  }
  // Benefits/payroll-tax/equity is a cost-type cut ACROSS every function, so
  // it necessarily intersects each functional pool's headcount.
  for (const functional of FUNCTIONAL_PERSONNEL_FAMILIES) {
    push(
      BENEFITS_FAMILY,
      functional,
      'overlapping',
      "Benefits/payroll-tax/equity is a cost-type cut across every SG&A function; it necessarily intersects each functional personnel pool's headcount."
    );
  }
  // Premium spend (contractual) and loss/claims spend (actuarial) are
  // mutually exclusive by construction; the claims/SIR pool excludes premium
  // amounts by definition.
  push(
    INSURANCE_PREMIUMS_FAMILY,
    CARGO_CLAIMS_SIR_FAMILY,
    'disjoint',
    'Premium spend and loss/claims spend are mutually exclusive by construction; the claims/SIR pool excludes premium amounts by definition.'
  );
  // Historically booked inside a broader existing pool, so existing rows may
  // sit in either.
  push(
    'finance_accounting_tax_treasury',
    'professional_fees',
    'overlapping',
    'Finance/accounting/tax/treasury fees were historically booked inside the broad external-fee pool; existing rows may sit in either.'
  );
  push(
    'legal_regulatory_compliance',
    'professional_fees',
    'overlapping',
    'Legal/regulatory/compliance fees were historically booked inside the broad external-fee pool; existing rows may sit in either.'
  );
  push(
    'office_purchased_services',
    'indirect_procurement',
    'overlapping',
    'Office supplies/purchased services and non-transport indirect vendor spend were historically booked together; existing rows may sit in either.'
  );
  push(
    'office_purchased_services',
    'shared_services',
    'overlapping',
    'Office supplies/purchased services and shared-services run-rate spend were historically booked together; existing rows may sit in either.'
  );
  push(
    'tech_software',
    'tech_cloud_infrastructure',
    'overlapping',
    'SaaS licensing and cloud infrastructure (IaaS) spend blur at the vendor line unless GL-separable.'
  );
}

function pushStructurallyDisjoint(push: PushDeclaration, allPre5571Families: readonly string[]): void {
  for (const isolated of STRUCTURALLY_DISJOINT_FROM_EVERYTHING) {
    const basis =
      isolated === BAD_DEBT_FAMILY
        ? 'Bad debt/credit-loss provision is a distinct GL treatment (accounts-receivable write-off), not a purchased or compensated cost, and shares dollars with no other lever family.'
        : 'Non-operating depreciation and amortization is a non-cash, non-operating charge by definition and shares dollars with no other lever family.';
    for (const existing of allPre5571Families) {
      push(isolated, existing, 'disjoint', basis);
    }
    for (const other of DEFAULT_OVERLAPPING_FAMILIES) {
      push(isolated, other, 'disjoint', basis);
    }
  }
  push(
    STRUCTURALLY_DISJOINT_FROM_EVERYTHING[0],
    STRUCTURALLY_DISJOINT_FROM_EVERYTHING[1],
    'disjoint',
    'Bad debt/credit-loss and non-operating D&A are distinct non-cash / non-purchased GL treatments and share no dollars with each other.'
  );
}

function pushDefaultOverlap(push: PushDeclaration, allPre5571Families: readonly string[]): void {
  const basis =
    'Structural disjointness is not proven from how this charge is booked (#5571 P1 ruling ' +
    'default); a de-confliction receipt is required before compositing rather than assuming ' +
    'additive dollars.';
  for (const newFamily of DEFAULT_OVERLAPPING_FAMILIES) {
    for (const existing of allPre5571Families) {
      push(newFamily, existing, 'overlapping', basis);
    }
  }
  for (let i = 0; i < DEFAULT_OVERLAPPING_FAMILIES.length; i += 1) {
    for (let j = i + 1; j < DEFAULT_OVERLAPPING_FAMILIES.length; j += 1) {
      push(DEFAULT_OVERLAPPING_FAMILIES[i], DEFAULT_OVERLAPPING_FAMILIES[j], 'overlapping', basis);
    }
  }
}

/**
 * @param allPre5571Families every lever family registered before #5571 (every
 * id `declaredOverlapRelation` already knew about) - the caller flattens its
 * own family arrays/singletons into one list; this file stays a pure
 * consumer of ids, never the arrays' owner.
 */
export function build5571SgaOverlapDeclarations(
  allPre5571Families: readonly string[]
): readonly LeverOverlapDeclaration[] {
  const declared = new Set<string>();
  const declarations: LeverOverlapDeclaration[] = [];
  const push: PushDeclaration = (familyA, familyB, relation, basis) => {
    const key = pairKey(familyA, familyB);
    if (declared.has(key)) return;
    declared.add(key);
    declarations.push(decl(familyA, familyB, relation, basis));
  };

  // Order matters: bespoke/named declarations run before the default loop so
  // the dedup guard lets them win over the generic default.
  pushNamedPairs(push);
  pushStructurallyDisjoint(push, allPre5571Families);
  pushDefaultOverlap(push, allPre5571Families);

  return Object.freeze(declarations);
}
